import * as THREE from 'three/webgpu';


/**
 * 将屏幕像素尺寸换算为世界尺寸。
 *
 * 设计说明：
 * 1. 透视相机按参考点的视空间深度和有效 FOV 换算，离轴位置不会错误放大；
 * 2. 方向箭头位于 cad-core，不能依赖 web-client 里的拉伸 composable；
 * 3. 计算过程直接读取相机矩阵元素，避免相机刷新热路径创建临时 Vector3；
 * 4. 当相机或容器不可用时，返回一个稳定兜底值，避免箭头尺寸退化为 0。
 *
 * @param camera 当前相机。
 * @param container 当前视口容器。
 * @param worldPoint 参考世界坐标。
 * @param pixelSize 目标屏幕像素尺寸。
 * @returns 对应世界尺寸。
 */
export function computeDirectionArrowWorldSizeFromPixels(
    camera: THREE.Camera | null | undefined,
    container: HTMLElement | null | undefined,
    worldPoint: THREE.Vector3,
    pixelSize: number,
): number {
    const normalizedPixelSize = Math.max(0, Number.isFinite(pixelSize) ? pixelSize : 0);
    const viewportHeight = container?.clientHeight ?? 0;

    if (!camera || !Number.isFinite(viewportHeight) || viewportHeight <= 0) {
        // 隐藏布局会短暂产生零高视口；这里返回稳定比例，禁止按 1px 视口换算出异常巨值。
        // 需要保留上一帧尺寸的有状态管理器会在调用本函数前直接跳过该轮刷新。
        return normalizedPixelSize * 0.01;
    }

    if (camera instanceof THREE.PerspectiveCamera) {
        // 固定像素尺寸取决于参考点沿相机前向的视空间深度，而不是到相机的球面距离；
        // 直接读取逆世界矩阵既修正离轴放大，又避免在相机刷新热路径创建临时 Vector3。
        camera.updateWorldMatrix(true, false);
        const viewMatrixElements = camera.matrixWorldInverse.elements;
        const minimumViewSpaceDepth = Number.isFinite(camera.near)
            ? Math.max(Number.EPSILON, camera.near)
            : Number.EPSILON;
        const viewSpaceDepth = Math.max(minimumViewSpaceDepth, Math.abs(
            viewMatrixElements[2] * worldPoint.x
            + viewMatrixElements[6] * worldPoint.y
            + viewMatrixElements[10] * worldPoint.z
            + viewMatrixElements[14],
        ));
        // getEffectiveFOV() 会纳入 PerspectiveCamera.zoom；使用原始 fov 会让程序化缩放后的手柄变大。
        const effectiveFovRadians = THREE.MathUtils.degToRad(camera.getEffectiveFOV());
        const worldHeight = 2 * Math.tan(effectiveFovRadians * 0.5) * viewSpaceDepth;
        return (worldHeight / viewportHeight) * normalizedPixelSize;
    }

    if (camera instanceof THREE.OrthographicCamera) {
        const worldHeight = (camera.top - camera.bottom) / Math.max(1e-6, camera.zoom);
        return (worldHeight / viewportHeight) * normalizedPixelSize;
    }

    return normalizedPixelSize * 0.01;
}

